import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Tabs,
  Tab,
  InputAdornment,
  Switch,
  FormControlLabel,
  AppBar,
  Toolbar,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Calculate as CalculateIcon,
  Receipt as ReceiptIcon,
  LocalOffer as LocalOfferIcon,
  Inventory2 as Inventory2Icon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as LocalShippingIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { reglaDescuentoAplicaAProducto } from 'utils/reglasDescuentos';
import ListadoCotizaciones, {
  PROGRAMA_LISTADO_COTIZACIONES,
} from './ListadoCotizaciones';
import VistaCotizacion from './VistaCotizacion';
import MantenimientoProductos from 'productosnoserializados/MantenimientoProductos';

export const PROGRAMA_REGISTRO_COTIZACIONES = 'registrocotizaciones';
export const PROGRAMA_GESTION_PRODUCTOS = 'gestionproductos';

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const itemHeadLabelSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const RegistroCotizaciones = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_REGISTRO_COTIZACIONES),
    [roleDetalles]
  );
  const puedeModificar = puedeLeer;
  const tienePermisoListado = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_LISTADO_COTIZACIONES),
    [roleDetalles]
  );
  const tienePermisoMantenimientoProductos = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_GESTION_PRODUCTOS),
    [roleDetalles]
  );

  const [productos, setProductos] = useState([]);
  const [reglasDescuentos, setReglasDescuentos] = useState([]);
  const [itemsCotizacion, setItemsCotizacion] = useState([]);
  const [tabPrincipal, setTabPrincipal] = useState(0);
  const [tabActiva, setTabActiva] = useState(0);
  const [cargando, setCargando] = useState(false);

  const [nombreCliente, setNombreCliente] = useState('');
  const [direccion, setDireccion] = useState('');
  const [direccionEnvio, setDireccionEnvio] = useState('');
  const [telefono, setTelefono] = useState('');
  const [nombreCotizacion, setNombreCotizacion] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const [dialogoItem, setDialogoItem] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [codigoItem, setCodigoItem] = useState('');
  const [descripcionItem, setDescripcionItem] = useState('');
  const [cantidadItem, setCantidadItem] = useState('1');
  const [precioItem, setPrecioItem] = useState('0');
  const [descuentoItem, setDescuentoItem] = useState('0');
  const [esInventario, setEsInventario] = useState(true);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);

  const [busquedaProducto, setBusquedaProducto] = useState('');

  const [vistaCotizacionAbierta, setVistaCotizacionAbierta] = useState(false);
  const [datosVistaCotizacion, setDatosVistaCotizacion] = useState({});
  const [autoImprimirCotizacion, setAutoImprimirCotizacion] = useState(false);
  const [dialogoMantenimientoProductos, setDialogoMantenimientoProductos] = useState(false);

  const subtotalAntesDescuentos = itemsCotizacion.reduce(
    (sum, item) => sum + (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio) || 0),
    0
  );
  const totalDescuentos = itemsCotizacion.reduce(
    (sum, item) => sum + (parseFloat(item.descuento) || 0),
    0
  );
  const subtotal = itemsCotizacion.reduce(
    (sum, item) => sum + (parseFloat(item.subtotal) || 0),
    0
  );
  const totalImpuestos = itemsCotizacion.reduce(
    (sum, item) => sum + (parseFloat(item.impuesto) || 0),
    0
  );
  const total = subtotal + totalImpuestos;

  const cargarProductos = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerproductos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: "productosnoserializados",
          localidad: localStorage.getItem('sucursal'),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setProductos(data.productos || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar productos');
      }
    } catch {
      showError('Error de conexión al cargar productos');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  const cargarReglasDescuentos = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerreglasdescuentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentos(data.reglas || []);
      }
    } catch (error) {
      console.error('Error al cargar reglas de descuento:', error);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor]);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      await Promise.all([cargarProductos(), cargarReglasDescuentos()]);
    } catch {
      showError('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  }, [cargarProductos, cargarReglasDescuentos, showError]);

  useEffect(() => {
    if (puedeLeer) cargarDatos();
  }, [puedeLeer, cargarDatos]);

  const aplicarReglasDescuento = (producto, cantidad = 1) => {
    let descuentoTotal = parseFloat(producto.descuento) || 0;
    const precioUnitario = parseFloat(producto.precio) || 0;
    let cantidadConDescuento = cantidad;
    const reglasAplicables = reglasDescuentos.filter((regla) => {
      if (regla.tipoAplicacion === 'producto') return reglaDescuentoAplicaAProducto(regla, producto);
      if (
        regla.tipoAplicacion === 'departamento' &&
        producto.departamento &&
        regla.departamento === producto.departamento
      ) {
        return true;
      }
      return false;
    });
    if (reglasAplicables.length > 0) {
      const regla = reglasAplicables[0];
      if (regla.tipoDescuento === 'porcentaje') {
        const porcentajeDescuento = parseFloat(regla.porcentaje) || 0;
        descuentoTotal = (precioUnitario * (porcentajeDescuento / 100)) * cantidad;
      } else if (regla.tipoDescuento === '2x1') {
        const pares = Math.floor(cantidad / 2);
        cantidadConDescuento = cantidad - pares;
        descuentoTotal = pares * precioUnitario;
      }
    }
    return { descuento: descuentoTotal, cantidad: cantidadConDescuento, precioUnitario };
  };

  const calcularSubtotalItem = (cantidad, precio, descuento) => {
    const cant = parseFloat(cantidad) || 0;
    const prec = parseFloat(precio) || 0;
    const desc = parseFloat(descuento) || 0;
    return Math.max(0, cant * prec - desc);
  };

  const calcularImpuestoItem = (subtotalParam) => subtotalParam * 0.18;

  const abrirDialogoItem = (item = null) => {
    if (item) {
      setItemEditando(item);
      setCodigoItem(item.codigo || '');
      setDescripcionItem(item.descripcion || '');
      setCantidadItem(item.cantidad?.toString() || '1');
      setPrecioItem(item.precio?.toString() || '0');
      setDescuentoItem(item.descuento?.toString() || '0');
      setEsInventario(item.es_inventario !== false);
      setProductoSeleccionado(
        productos.find((p) => p.codigo === item.producto_codigo) || null
      );
    } else {
      setItemEditando(null);
      setCodigoItem('');
      setDescripcionItem('');
      setCantidadItem('1');
      setPrecioItem('0');
      setDescuentoItem('0');
      setEsInventario(tabActiva === 0);
      setProductoSeleccionado(null);
    }
    setDialogoItem(true);
  };

  const cerrarDialogoItem = () => {
    setDialogoItem(false);
    setItemEditando(null);
    setCodigoItem('');
    setDescripcionItem('');
    setCantidadItem('1');
    setPrecioItem('0');
    setDescuentoItem('0');
    setProductoSeleccionado(null);
  };

  const agregarProducto = (producto) => {
    if (!puedeModificar) {
      showError('No tiene permisos para registrar cotizaciones.');
      return;
    }
    const esServicio = String(producto.tipoproducto || '').toLowerCase() === 'servicio';
    const esInventarioItem = !esServicio;
    const itemExistente = itemsCotizacion.find(
      (item) =>
        item.producto_codigo === producto.codigo && item.es_inventario === esInventarioItem
    );
    const descuentoInfo = aplicarReglasDescuento(producto, 1);
    const descuentoProducto = descuentoInfo.descuento;
    const precio = parseFloat(producto.precio) || 0;

    if (itemExistente) {
      const nuevaCantidad = parseFloat(itemExistente.cantidad) + 1;
      const descuentoInfoActualizado = aplicarReglasDescuento(producto, nuevaCantidad);
      const nuevoDescuento = descuentoInfoActualizado.descuento;
      const subtotalItem = Math.max(
        0,
        nuevaCantidad * parseFloat(itemExistente.precio) - nuevoDescuento
      );
      const impuestoItem = calcularImpuestoItem(subtotalItem);

      setItemsCotizacion(
        itemsCotizacion.map((item) =>
          item.id === itemExistente.id
            ? {
                ...item,
                cantidad: nuevaCantidad,
                descuento: nuevoDescuento,
                subtotal: subtotalItem,
                impuesto: impuestoItem,
                nombre_impuesto: item.nombre_impuesto || 'ITBIS 18%',
              }
            : item
        )
      );
    } else {
      const subtotalItem = Math.max(0, 1 * precio - descuentoProducto);
      const impuestoItem = calcularImpuestoItem(subtotalItem);
      const nuevoItem = {
        id: Date.now(),
        codigo: producto.codigo,
        descripcion: producto.descripcion,
        cantidad: 1,
        precio: precio,
        descuento: descuentoProducto,
        impuesto: impuestoItem,
        nombre_impuesto: 'ITBIS 18%',
        subtotal: subtotalItem,
        tipo: esServicio ? 'servicio' : 'producto',
        es_inventario: esInventarioItem,
        producto_codigo: producto.codigo,
      };
      setItemsCotizacion([...itemsCotizacion, nuevoItem]);
    }
    setBusquedaProducto('');
  };

  const guardarItem = () => {
    if (!descripcionItem.trim()) {
      showError('La descripción del item es requerida');
      return;
    }

    const cantidad = parseFloat(cantidadItem) || 0;
    const precio = parseFloat(precioItem) || 0;
    let descuento = 0;

    if (cantidad <= 0) {
      showError('La cantidad debe ser mayor a cero');
      return;
    }
    if (precio <= 0) {
      showError('El precio debe ser mayor a cero');
      return;
    }

    if (esInventario && productoSeleccionado) {
      const descuentoInfo = aplicarReglasDescuento(productoSeleccionado, cantidad);
      descuento = descuentoInfo.descuento;
    } else {
      descuento = parseFloat(descuentoItem) || 0;
    }

    const subtotalBruto = cantidad * precio;
    const subtotalCalc = Math.max(0, subtotalBruto - descuento);
    const impuesto = calcularImpuestoItem(subtotalCalc);

    const nuevoItem = {
      id: itemEditando ? itemEditando.id : Date.now(),
      codigo: codigoItem || null,
      descripcion: descripcionItem.trim(),
      cantidad: cantidad,
      precio: precio,
      descuento: descuento,
      impuesto: impuesto,
      nombre_impuesto: 'ITBIS 18%',
      subtotal: subtotalCalc,
      tipo: 'producto',
      es_inventario: esInventario,
      producto_codigo: esInventario && productoSeleccionado ? productoSeleccionado.codigo : null,
    };

    if (itemEditando) {
      setItemsCotizacion(
        itemsCotizacion.map((item) => (item.id === itemEditando.id ? nuevoItem : item))
      );
    } else {
      setItemsCotizacion([...itemsCotizacion, nuevoItem]);
    }

    cerrarDialogoItem();
  };

  const eliminarItem = (itemId) => {
    setItemsCotizacion(itemsCotizacion.filter((item) => item.id !== itemId));
  };

  const limpiarCotizacion = () => {
    if (window.confirm('¿Está seguro de limpiar la cotización? Se perderán todos los datos.')) {
      setNombreCliente('');
      setDireccion('');
      setDireccionEnvio('');
      setTelefono('');
      setNombreCotizacion('');
      setObservaciones('');
      setItemsCotizacion([]);
    }
  };

  const guardarCotizacion = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar cotizaciones.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!nombreCliente.trim()) {
      showError('El nombre del cliente es requerido');
      return;
    }
    if (itemsCotizacion.length === 0) {
      showError('Debe agregar al menos un item a la cotización');
      return;
    }

    try {
      const configRes = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo_documento: 'COTIZACION',
        }),
      });
      const configData = await configRes.json().catch(() => ({}));
      if (configData.configuracionGuardada === false) {
        showError('Debe configurar la plantilla de cotización en Configuración (tipo COTIZACIÓN) antes de guardar.');
        return;
      }
    } catch {
      showError('No se pudo verificar la configuración de cotización');
      return;
    }

    try {
      const response = await fetch(servidor + '/api/facturabackend_guardarcotizacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          cotizacion: {
            nombre: nombreCotizacion.trim() || undefined,
            cliente: nombreCliente.trim(),
            direccion: direccion.trim(),
            direccion_envio: direccionEnvio.trim(),
            telefono: telefono.trim(),
            fecha: new Date().toISOString().split('T')[0],
            subtotal: subtotal,
            impuestos: totalImpuestos,
            total: total,
            observaciones: observaciones.trim(),
            items: itemsCotizacion,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(`Cotización Nº ${data.numero} guardada exitosamente`);
        setDatosVistaCotizacion({
          numero: data.numero,
          cliente: nombreCliente.trim(),
          direccion: direccion.trim(),
          direccion_envio: direccionEnvio.trim(),
          telefono: telefono.trim(),
          items: itemsCotizacion,
          subtotalAntesDescuentos,
          totalDescuentos,
          totalImpuestos,
          subtotal,
          total,
          fecha: new Date().toISOString().split('T')[0],
          observaciones: observaciones.trim(),
          id_empresa: selectedCompany,
          empresa: selectedCompany?.empresa || selectedCompany?.nombre || '',
        });
        setAutoImprimirCotizacion(true);
        setVistaCotizacionAbierta(true);
        setNombreCliente('');
        setDireccion('');
        setDireccionEnvio('');
        setTelefono('');
        setNombreCotizacion('');
        setObservaciones('');
        setItemsCotizacion([]);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al guardar la cotización');
      }
    } catch {
      showError('Error de conexión al guardar cotización');
    }
  };

  const terminoBusqueda =
    typeof busquedaProducto === 'string' ? busquedaProducto.trim().toLowerCase() : '';
  const coincideBusqueda = (p) => {
    if (!p) return false;
    const desc = (p.descripcion && String(p.descripcion).toLowerCase()) || '';
    const cod = (p.codigo != null && String(p.codigo).toLowerCase()) || '';
    return !terminoBusqueda || desc.includes(terminoBusqueda) || cod.includes(terminoBusqueda);
  };
  const esTipoServicio = (p) =>
    String(p?.tipoproducto || '').toLowerCase() === 'servicio';

  const productosFiltrados = Array.isArray(productos)
    ? productos.filter((p) => !esTipoServicio(p) && coincideBusqueda(p))
    : [];
  const serviciosFiltrados = Array.isArray(productos)
    ? productos.filter((p) => esTipoServicio(p) && coincideBusqueda(p))
    : [];

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Cotizaciones"
          subtitle={nombreNegocio ? `${nombreNegocio} · Registro` : 'Registro'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «{PROGRAMA_REGISTRO_COTIZACIONES}» en roles para registrar cotizaciones.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cotizaciones"
        subtitle={nombreNegocio ? `${nombreNegocio} · Registro` : 'Registro'}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 2 }}>
          <Tabs
            value={tabPrincipal}
            onChange={(e, v) => setTabPrincipal(v)}
            sx={{
              px: { xs: 1.5, md: 2.5 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                minHeight: 48,
                color: FP.color.textSecondary,
                letterSpacing: '0.02em',
              },
              '& .Mui-selected': { color: FP.color.navy },
              '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 2.5 },
            }}
          >
            <Tab icon={<ReceiptIcon fontSize="small" />} iconPosition="start" label="Registro" />
            {tienePermisoListado && (
              <Tab icon={<LocalOfferIcon fontSize="small" />} iconPosition="start" label="Seguimiento" />
            )}
          </Tabs>
        </Card>

        {tabPrincipal === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Card sx={{ ...cardShellSx, display: 'flex', flexDirection: 'column', minHeight: '600px' }}>
                <CardContent
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    p: 2.5,
                    minHeight: 0,
                  }}
                >
                  <Box sx={{ flexShrink: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mb: 2,
                        pb: 1.2,
                        borderBottom: `1px solid ${FP.color.border}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.2,
                          bgcolor: FP.color.accentBlueSoft,
                          color: FP.color.accentBlue,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography
                        sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}
                      >
                        Datos del cliente
                      </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Nombre del cliente"
                          value={nombreCliente}
                          onChange={(e) => setNombreCliente(e.target.value)}
                          required
                          disabled={!puedeModificar}
                          InputLabelProps={{ shrink: true }}
                          sx={fpInputSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Dirección del cliente"
                          value={direccion}
                          onChange={(e) => setDireccion(e.target.value)}
                          placeholder="Dirección del cliente"
                          disabled={!puedeModificar}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationOnIcon sx={{ color: FP.color.textMuted, fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={fpInputSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Dirección de envío / entrega"
                          value={direccionEnvio}
                          onChange={(e) => setDireccionEnvio(e.target.value)}
                          placeholder="Donde se enviarán los artículos o el servicio"
                          disabled={!puedeModificar}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocalShippingIcon sx={{ color: FP.color.textMuted, fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={fpInputSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Teléfono"
                          value={telefono}
                          onChange={(e) => setTelefono(e.target.value)}
                          disabled={!puedeModificar}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon sx={{ color: FP.color.textMuted, fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={fpInputSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Referencia / asunto (opcional)"
                          value={nombreCotizacion}
                          onChange={(e) => setNombreCotizacion(e.target.value)}
                          placeholder="Ej: Cotización proyecto X"
                          disabled={!puedeModificar}
                          InputLabelProps={{ shrink: true }}
                          sx={fpInputSx}
                          helperText="Nº de cotización se asignará automáticamente al guardar"
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2, borderColor: FP.color.border }} />

                  <Tabs
                    value={tabActiva}
                    onChange={(e, v) => {
                      setTabActiva(v);
                      setEsInventario(v === 0);
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        minHeight: 44,
                        color: FP.color.textSecondary,
                      },
                      '& .Mui-selected': { color: FP.color.navy },
                      '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 2.5 },
                    }}
                  >
                    <Tab icon={<Inventory2Icon fontSize="small" />} iconPosition="start" label="Productos inventario" />
                    <Tab icon={<BuildIcon fontSize="small" />} iconPosition="start" label="Servicios" />
                  </Tabs>

                  {(() => {
                    const enPestanaServicios = tabActiva === 1;
                    const listaActiva = enPestanaServicios
                      ? serviciosFiltrados
                      : productosFiltrados;
                    const placeholderBusqueda = enPestanaServicios
                      ? 'Buscar servicio…'
                      : 'Buscar producto…';
                    const mensajeVacio = enPestanaServicios
                      ? 'No hay servicios disponibles'
                      : 'No hay productos disponibles';
                    return (
                      <>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 2, alignItems: 'stretch' }}
                        >
                          <TextField
                            fullWidth
                            placeholder={placeholderBusqueda}
                            value={busquedaProducto}
                            onChange={(e) => setBusquedaProducto(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: FP.color.textMuted }} />
                                </InputAdornment>
                              ),
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1, ...fpInputSx }}
                          />
                          {tienePermisoMantenimientoProductos && (
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => setDialogoMantenimientoProductos(true)}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 1.4,
                                fontWeight: 700,
                                borderColor: FP.color.border,
                                color: FP.color.textPrimary,
                                bgcolor: '#FFFFFF',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: FP.color.borderStrong,
                                  bgcolor: FP.color.surfaceAlt,
                                },
                              }}
                            >
                              {enPestanaServicios ? 'Nuevo servicio' : 'Nuevo producto'}
                            </Button>
                          )}
                        </Stack>

                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            maxHeight: '45vh',
                            overflow: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {cargando ? (
                            <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                              Cargando…
                            </Typography>
                          ) : listaActiva.length === 0 ? (
                            <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                              {mensajeVacio}
                            </Typography>
                          ) : (
                            <Grid container spacing={1.2}>
                              {listaActiva.map((producto) => (
                                <Grid item xs={6} sm={4} key={producto.codigo}>
                                  <Card
                                    onClick={() => agregarProducto(producto)}
                                    sx={{
                                      cursor: puedeModificar ? 'pointer' : 'not-allowed',
                                      borderRadius: 2,
                                      transition: 'all .15s ease',
                                      bgcolor: FP.color.cardBg,
                                      border: `1px solid ${
                                        enPestanaServicios ? FP.color.warning : FP.color.border
                                      }`,
                                      boxShadow: 'none',
                                      '&:hover': puedeModificar
                                        ? {
                                            borderColor: FP.color.accentBlue,
                                            boxShadow: FP.shadow.soft,
                                            transform: 'translateY(-2px)',
                                          }
                                        : {},
                                    }}
                                  >
                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.6}
                                        sx={{ mb: 0.4 }}
                                      >
                                        {enPestanaServicios ? (
                                          <BuildIcon
                                            sx={{ fontSize: 12, color: FP.color.warning }}
                                          />
                                        ) : (
                                          <Inventory2Icon
                                            sx={{ fontSize: 12, color: FP.color.accentBlue }}
                                          />
                                        )}
                                        <Typography
                                          sx={{
                                            color: FP.color.textMuted,
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                          }}
                                        >
                                          {producto.codigo}
                                        </Typography>
                                      </Stack>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          mb: 0.5,
                                          color: FP.color.textPrimary,
                                          lineHeight: 1.3,
                                          fontSize: '0.85rem',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {producto.descripcion}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          color: FP.color.navy,
                                          fontWeight: 800,
                                          fontSize: '1rem',
                                          fontVariantNumeric: 'tabular-nums',
                                        }}
                                      >
                                        ${parseFloat(producto.precio || 0).toFixed(2)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </Box>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card sx={{ ...cardShellSx, display: 'flex', flexDirection: 'column', minHeight: '600px' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 2.5 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mb: 1.5,
                      pb: 1.2,
                      borderBottom: `1px solid ${FP.color.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1.2,
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.accentBlue,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ShoppingCartIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
                      Items de la cotización
                    </Typography>
                  </Stack>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 2,
                      overflow: 'auto',
                      maxHeight: '45vh',
                      mb: 2,
                    }}
                  >
                    <Table size="small" sx={{ minWidth: 720 }}>
                      <TableBody>
                        <TableRow
                          sx={{
                            '& td': {
                              position: 'sticky',
                              top: 0,
                              zIndex: 2,
                              border: 'none',
                              borderBottom: `2px solid ${FP.color.border}`,
                              bgcolor: FP.color.surfaceAlt,
                            },
                          }}
                        >
                          <TableCell sx={itemHeadLabelSx}>Tipo</TableCell>
                          <TableCell sx={itemHeadLabelSx}>Descripción</TableCell>
                          <TableCell align="center" sx={itemHeadLabelSx}>Cant.</TableCell>
                          <TableCell align="right" sx={itemHeadLabelSx}>Precio</TableCell>
                          <TableCell align="right" sx={itemHeadLabelSx}>Desc.</TableCell>
                          <TableCell align="right" sx={itemHeadLabelSx}>Subtotal</TableCell>
                          <TableCell align="right" sx={itemHeadLabelSx}>Impuesto</TableCell>
                          <TableCell align="center" sx={itemHeadLabelSx}>Acción</TableCell>
                        </TableRow>
                        {itemsCotizacion.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ color: FP.color.textSecondary, py: 4 }}>
                              No hay items en la cotización
                            </TableCell>
                          </TableRow>
                        ) : (
                          itemsCotizacion.map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={item.es_inventario ? 'Inventario' : 'Servicio'}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    bgcolor: item.es_inventario
                                      ? FP.color.accentBlueSoft
                                      : FP.color.warningSoft,
                                    color: item.es_inventario ? FP.color.accentBlue : FP.color.warning,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 500,
                                  color: FP.color.textPrimary,
                                  fontSize: '0.85rem',
                                }}
                              >
                                {item.descripcion}
                                {item.codigo && (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: FP.color.textMuted }}
                                  >
                                    Código: {item.codigo}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 700,
                                  color: FP.color.textPrimary,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {parseFloat(item.cantidad).toFixed(2)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: FP.color.textPrimary,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                ${parseFloat(item.precio).toFixed(2)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color:
                                    parseFloat(item.descuento || 0) > 0
                                      ? FP.color.danger
                                      : FP.color.textMuted,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                ${(parseFloat(item.descuento) || 0).toFixed(2)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 800,
                                  color: FP.color.navy,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                ${parseFloat(item.subtotal).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.72rem' }}>
                                  {item.nombre_impuesto || 'ITBIS 18%'}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: FP.color.textPrimary,
                                    fontVariantNumeric: 'tabular-nums',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  ${(parseFloat(item.impuesto) || 0).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => abrirDialogoItem(item)}
                                  disabled={!puedeModificar}
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                    mr: 0.5,
                                  }}
                                >
                                  <CalculateIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => eliminarItem(item.id)}
                                  disabled={!puedeModificar}
                                  sx={{
                                    color: FP.color.danger,
                                    '&:hover': { bgcolor: FP.color.dangerSoft },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TextField
                    fullWidth
                    label="Observaciones"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    multiline
                    rows={2}
                    disabled={!puedeModificar}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2, ...fpInputSx, '& .MuiOutlinedInput-root': { ...fpInputSx['& .MuiOutlinedInput-root'], height: 'auto', minHeight: 46 } }}
                  />

                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: FP.color.surfaceAlt,
                      border: `1px solid ${FP.color.border}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                        Subtotal:
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: FP.color.textPrimary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${subtotalAntesDescuentos.toFixed(2)}
                      </Typography>
                    </Box>
                    {totalDescuentos > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                          Descuentos:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: FP.color.danger,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          −${totalDescuentos.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                        Impuestos:
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: FP.color.textPrimary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${totalImpuestos.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5, borderColor: FP.color.border }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.6,
                        borderRadius: 1.6,
                        bgcolor: FP.color.navy,
                        color: '#FFFFFF',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Total</Typography>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.4rem',
                          fontVariantNumeric: 'tabular-nums',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        ${total.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.4 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={limpiarCotizacion}
                      disabled={!puedeModificar}
                      fullWidth
                      sx={{
                        py: 1.2,
                        textTransform: 'none',
                        borderRadius: 1.4,
                        fontWeight: 700,
                        borderColor: FP.color.border,
                        color: FP.color.textPrimary,
                        bgcolor: '#FFFFFF',
                        '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
                      }}
                    >
                      Limpiar
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                      onClick={guardarCotizacion}
                      fullWidth
                      disabled={
                        itemsCotizacion.length === 0 ||
                        !nombreCliente.trim() ||
                        !puedeModificar ||
                        !usuarioLog
                      }
                      sx={{
                        py: 1.2,
                        textTransform: 'none',
                        borderRadius: 1.4,
                        fontWeight: 700,
                        bgcolor: FP.color.navy,
                        color: '#FFFFFF',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                        '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                      }}
                    >
                      Guardar cotización
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <ListadoCotizaciones autoCargar={false} showPageHeader={false} />
        )}
      </Box>

      <Dialog
        fullScreen
        open={dialogoMantenimientoProductos}
        onClose={() => {
          setDialogoMantenimientoProductos(false);
          cargarProductos();
        }}
      >
        <AppBar position="static" elevation={0} sx={{ bgcolor: FP.color.navy }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                setDialogoMantenimientoProductos(false);
                cargarProductos();
              }}
              sx={{ mr: 2 }}
              aria-label="cerrar"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Mantenimiento de productos y servicios
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ overflow: 'auto', flex: 1, p: 2, bgcolor: FP.color.surface }}>
          <MantenimientoProductos />
        </Box>
      </Dialog>

      <VistaCotizacion
        open={vistaCotizacionAbierta}
        onClose={() => {
          setVistaCotizacionAbierta(false);
          setAutoImprimirCotizacion(false);
        }}
        cotizacionData={datosVistaCotizacion}
        autoPrint={autoImprimirCotizacion}
      />

      <Dialog
        open={dialogoItem}
        onClose={cerrarDialogoItem}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            {itemEditando ? 'Editar item' : 'Agregar item'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <FormControlLabel
            control={
              <Switch
                checked={esInventario}
                onChange={(e) => setEsInventario(e.target.checked)}
                disabled={!!productoSeleccionado}
              />
            }
            label={esInventario ? 'Item de inventario' : 'Servicio'}
            sx={{ mb: 2 }}
          />

          {!productoSeleccionado && (
            <TextField
              fullWidth
              label="Código (opcional)"
              value={codigoItem}
              onChange={(e) => setCodigoItem(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2, ...fpInputSx }}
            />
          )}

          <TextField
            fullWidth
            label="Descripción"
            value={descripcionItem}
            onChange={(e) => setDescripcionItem(e.target.value)}
            required
            disabled={!!productoSeleccionado}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, ...fpInputSx }}
          />

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Cantidad"
                type="number"
                value={cantidadItem}
                onChange={(e) => setCantidadItem(e.target.value)}
                required
                inputProps={{ min: 0.01, step: 0.01 }}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Precio"
                type="number"
                value={precioItem}
                onChange={(e) => setPrecioItem(e.target.value)}
                required
                inputProps={{ min: 0, step: 0.01 }}
                disabled={!!productoSeleccionado}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Descuento"
                type="number"
                value={descuentoItem}
                onChange={(e) => setDescuentoItem(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: FP.color.surfaceAlt,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Cálculo
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.85rem', color: FP.color.textSecondary }}>
                Subtotal:
              </Typography>
              <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: FP.color.textPrimary }}>
                ${calcularSubtotalItem(cantidadItem, precioItem, descuentoItem).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.85rem', color: FP.color.textSecondary }}>
                Impuesto (18%):
              </Typography>
              <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: FP.color.textPrimary }}>
                ${calcularImpuestoItem(calcularSubtotalItem(cantidadItem, precioItem, descuentoItem)).toFixed(2)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1, borderColor: FP.color.border }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>Total:</Typography>
              <Typography sx={{ fontWeight: 800, color: FP.color.navy, fontVariantNumeric: 'tabular-nums' }}>
                ${(
                  calcularSubtotalItem(cantidadItem, precioItem, descuentoItem) +
                  calcularImpuestoItem(calcularSubtotalItem(cantidadItem, precioItem, descuentoItem))
                ).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogoItem}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={guardarItem}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            {itemEditando ? 'Modificar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistroCotizaciones;
